import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useBackupsQuery,
  useCreateBackupMutation,
  useSystemBusy,
} from '../../../hooks/useSystemQueries';
import { parseApiError } from '../../../utils/parseApiError';
import { RestoreDialog } from './RestoreDialog';
import { RunHistory } from './RunHistory';

const formatBytes = (n: number) =>
  n >= 1024 ** 3
    ? `${(n / 1024 ** 3).toFixed(2)} GB`
    : n >= 1024 ** 2
      ? `${(n / 1024 ** 2).toFixed(1)} MB`
      : `${Math.round(n / 1024)} KB`;

/** Backups in the bucket: create one, or restore one over the whole database. */
export const BackupsTab = () => {
  const { t } = useTranslation();
  const backups = useBackupsQuery();
  const create = useCreateBackupMutation();
  const busy = useSystemBusy();
  const [restoring, setRestoring] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const doCreate = async () => {
    setCreated(null);
    try {
      const run = await create.mutateAsync();
      setCreated(String(run?.summary?.fileName ?? ''));
    } catch {
      // Shown from create.error.
    }
  };

  const list = backups.data ?? [];
  return (
    <div className="system-tab">
      <section className="team-panel">
        <header className="team-panel-header">
          <h3 className="team-panel-title">
            {t('dashboard.system.backups.title', { count: list.length })}
          </h3>
          <button type="button" className="primary-action-btn" disabled={busy} onClick={() => void doCreate()}>
            <span className="material-symbols-outlined" aria-hidden="true">
              {create.isPending ? 'hourglass_empty' : 'cloud_upload'}
            </span>
            {create.isPending ? t('dashboard.system.backups.creating') : t('dashboard.system.backups.create')}
          </button>
        </header>
        {(create.error || backups.error || created) && (
          <div className="system-panel-body">
            {create.error && (
              <p className="role-form-error" role="alert">{parseApiError(create.error).message}</p>
            )}
            {backups.error && (
              <p className="role-form-error" role="alert">{parseApiError(backups.error).message}</p>
            )}
            {created && (
              <p className="system-success" role="status">
                {t('dashboard.system.backups.created', { file: created })}
              </p>
            )}
          </div>
        )}
        {list.length === 0 ? (
          <p className="team-muted team-panel-empty">{t('dashboard.system.backups.empty')}</p>
        ) : (
          <ul className="system-list">
            {list.map((b) => (
              <li key={b.name} className="system-row">
                <div className="system-row-main">
                  <div className="system-row-title">{b.name}</div>
                  <div className="team-muted">
                    {new Date(b.createdAt).toLocaleString()} — {formatBytes(b.size)}
                  </div>
                </div>
                <button
                  type="button"
                  className="secondary-action-btn"
                  disabled={busy}
                  onClick={() => setRestoring(b.name)}
                >
                  {t('dashboard.system.backups.restore')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <RunHistory kind="restore" />
      <RestoreDialog fileName={restoring} onClose={() => setRestoring(null)} />
    </div>
  );
};
